import { useFormContext } from '@calendly/form'
import { useCallback, useMemo } from 'react'

import { getCurrentOrganizer } from 'components/event_type_edit'
import { useETEditorFormikPoolingType } from 'services/event_types'

import { getUserPools, isOrganizableConferenceLocation } from '../utils'

const emptyObject = {}
const emptyArray = []
export const useOrganizer = (eventType = emptyObject, kind, locationConfigurations = emptyArray) => {
  const { id, isTeam, isAdhoc, expected_calendar_pusher_membership_id: expectedCalendarPusherMembershipId } = eventType
  const isNewEventType = !id || isAdhoc
  const organizableConferenceLocation = isOrganizableConferenceLocation(kind)
  const { values, values: { memberships = emptyArray }, setFieldValue } = useFormContext()
  const userPools = getUserPools(values)
  const { isCollective, isCollectiveRoundRobin } = useETEditorFormikPoolingType()
  const enabledHosts = useMemo(() => memberships.filter(({ enabled, _destroy }) => enabled && !_destroy), [memberships])

  const organizerMemberships = useMemo(() => {
    if (isNewEventType && isCollective) return enabledHosts
    if (isTeam) return enabledHosts

    if (isCollectiveRoundRobin && userPools.some((pool) => pool.pooling_type === 'collective')) {
      const collectiveUserPool = userPools.find((pool) => pool.pooling_type === 'collective')
      const userPoolUuid = collectiveUserPool.uuid || collectiveUserPool.key
      return enabledHosts.filter((membership) => membership.user_pool_uid === userPoolUuid)
    }

    return memberships
  }, [enabledHosts, isCollective, isCollectiveRoundRobin, isNewEventType, isTeam, memberships, userPools])

  const currentOrganizer = useMemo(() => getCurrentOrganizer(
    organizerMemberships,
    kind,
    expectedCalendarPusherMembershipId,
    locationConfigurations
  ), [expectedCalendarPusherMembershipId, kind, locationConfigurations, organizerMemberships])

  const isSameMembership = useCallback((membership1, membership2) => {
    if (isAdhoc) return membership1.user_id === membership2.user_id
    return ((!!membership1.organization_membership_id && membership1.organization_membership_id === membership2.organization_membership_id)
      || (!!membership1.organization_invitation_id && membership1.organization_invitation_id === membership2.organization_invitation_id))
  }, [isAdhoc])

  const updateOrganizer = useCallback((membership) => {
    const updatedMemberships = memberships.map((m) => ({
      ...m,
      location_organizer: isSameMembership(m, membership)
    }))
    setFieldValue('memberships', updatedMemberships)
  }, [memberships, isSameMembership, setFieldValue])

  const updateLocationConfigurations = useCallback((membership) => {
    const updatedLocationConfigurations = locationConfigurations.map((config) => {
      const updateProperties = {}
      if (config.kind === kind) {
        updateProperties.organizer_user_id = membership.user_id
      }
      return {
        ...config,
        ...updateProperties
      }
    })
    setFieldValue('locationConfigurations', updatedLocationConfigurations)
  }, [locationConfigurations, kind, setFieldValue])

  return {
    organizableConferenceLocation,
    organizerMemberships,
    currentOrganizer,
    updateOrganizer,
    updateLocationConfigurations
  }
}
